import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useLanguage } from '../../context/LanguageContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { ArrowLeft, Calendar, Image as ImageIcon, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { AutoTranslate } from '../../hooks/useAutoTranslate';
import { getAlbum, getAlbumPhotos } from '../../services/api';

const getLocalized = (obj, lang) => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] || obj.sv || obj.en || Object.values(obj)[0] || '';
};

const formatEventDate = (iso, lang) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === 'sv' ? 'sv-SE' : lang, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const AlbumDetailPage = () => {
  const { id } = useParams();
  const { language, isRTL } = useLanguage();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // Lightbox navigation
  const selectedIndex = selectedPhoto ? photos.findIndex((p) => p.id === selectedPhoto.id) : -1;
  const goPrev = () => {
    if (selectedIndex > 0) setSelectedPhoto(photos[selectedIndex - 1]);
  };
  const goNext = () => {
    if (selectedIndex >= 0 && selectedIndex < photos.length - 1) setSelectedPhoto(photos[selectedIndex + 1]);
  };
  useEffect(() => {
    if (!selectedPhoto) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') setSelectedPhoto(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedPhoto, photos]);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const [a, p] = await Promise.all([getAlbum(id), getAlbumPhotos(id)]);
        setAlbum(a);
        const list = p || [];
        // Prepend cover as first tile if it's not already among the photos (dedup by image_url)
        if (a?.cover_image_url && !list.some((x) => x.image_url === a.cover_image_url)) {
          list.unshift({
            id: `cover-${a.id}`,
            image_url: a.cover_image_url,
            title: a.title,
            description: null,
            isCover: true,
          });
        }
        setPhotos(list);
      } catch (err) {
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          console.error('Error loading album:', err);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const heroImg = album?.cover_image_url || photos[0]?.image_url || 'https://images.unsplash.com/photo-1519741497674-611481863552?w=1920';

  if (loading) {
    return (
      <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'}>
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-24 text-center text-gray-500" data-testid="album-detail-loading">
          <AutoTranslate>Laddar album...</AutoTranslate>
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !album) {
    return (
      <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'}>
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="album-detail-not-found">
          <h1 className="text-3xl font-serif font-bold text-gray-800 mb-3">
            <AutoTranslate>Album hittades inte</AutoTranslate>
          </h1>
          <Link to="/forsamling/album">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              <AutoTranslate>Tillbaka till alla album</AutoTranslate>
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="album-detail-page">
      <Navbar />

      {/* Hero */}
      <section className="themed-hero relative h-96 overflow-hidden">
        <img src={heroImg} alt={getLocalized(album.title, language)} className="w-full h-full object-cover" />
        <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-black/40 to-black/70' : 'from-black/70 to-black/40'} flex items-end`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-10 ${isRTL ? 'text-right' : ''}`}>
            <Link
              to="/forsamling/album"
              className="inline-flex items-center gap-2 text-white hover:text-yellow-300 text-base sm:text-lg font-medium mb-4 transition-colors"
              data-testid="back-to-albums"
            >
              <ArrowLeft className="w-5 h-5" />
              <AutoTranslate>Alla album</AutoTranslate>
            </Link>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-3 fade-in">
              {getLocalized(album.title, language)}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-gray-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatEventDate(album.event_date, language)}</span>
              </div>
              <div className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                <span>{album.photo_count || photos.length} <AutoTranslate>bilder</AutoTranslate></span>
              </div>
            </div>
            {album.description && getLocalized(album.description, language) && (
              <p className="responsive-hero-subtitle mt-3 max-w-3xl text-base sm:text-lg text-gray-100">
                {getLocalized(album.description, language)}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Photos grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {photos.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="album-no-photos">
              <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p><AutoTranslate>Inga bilder ännu i detta album.</AutoTranslate></p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="album-photos-grid">
              {photos.map((photo) => (
                <button
                  key={photo.id}
                  onClick={() => setSelectedPhoto(photo)}
                  className="group block relative aspect-square overflow-hidden rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  data-testid={`photo-thumb-${photo.id}`}
                >
                  <img
                    src={photo.image_url}
                    alt={getLocalized(photo.title, language)}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!selectedPhoto} onOpenChange={(open) => !open && setSelectedPhoto(null)}>
        <DialogContent className="max-w-5xl bg-black/95 border-0 p-0" data-testid="album-lightbox">
          {selectedPhoto && (
            <div className="relative">
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-4 right-4 z-20 text-white bg-black/60 hover:bg-black/80 rounded-full p-2 transition-colors"
                aria-label="Close"
                data-testid="lightbox-close"
              >
                <X className="w-5 h-5" />
              </button>

              {selectedIndex > 0 && (
                <button
                  onClick={goPrev}
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 text-white bg-black/50 hover:bg-black/80 rounded-full p-2 sm:p-3 transition-colors"
                  aria-label="Previous photo"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
                </button>
              )}

              {selectedIndex >= 0 && selectedIndex < photos.length - 1 && (
                <button
                  onClick={goNext}
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 text-white bg-black/50 hover:bg-black/80 rounded-full p-2 sm:p-3 transition-colors"
                  aria-label="Next photo"
                  data-testid="lightbox-next"
                >
                  <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
                </button>
              )}

              <img
                src={selectedPhoto.image_url}
                alt={getLocalized(selectedPhoto.title, language)}
                className="w-full max-h-[85vh] object-contain"
              />

              {selectedIndex >= 0 && (
                <div className="absolute top-4 left-4 z-10 text-white text-sm bg-black/60 rounded-full px-3 py-1">
                  {selectedIndex + 1} / {photos.length}
                </div>
              )}

              {(getLocalized(selectedPhoto.title, language) || getLocalized(selectedPhoto.description, language)) && (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
                  {getLocalized(selectedPhoto.title, language) && (
                    <h3 className="text-lg font-serif font-bold mb-1">
                      {getLocalized(selectedPhoto.title, language)}
                    </h3>
                  )}
                  {getLocalized(selectedPhoto.description, language) && (
                    <p className="text-sm text-gray-200">{getLocalized(selectedPhoto.description, language)}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AlbumDetailPage;
